/**
 * Факты заказа для верхней сводки МСК Газобетона: сколько просил клиент, сколько
 * фактически отгрузим и на сколько объём вырос при округлении до полного поддона.
 *
 * Своих расчётов нет. Фактические значения берутся из уже собранного состава груза
 * блоков КП (`gazCargoItems`) — тем же раскладом описаны карточки машин. Запрошенный
 * объём — то, что менеджер ввёл в разделе «Товары».
 */

const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

const sumBy = (items, key) => items.reduce((sum, item) => {
  const value = Number(item?.[key]);
  return Number.isFinite(value) ? sum + value : sum;
}, 0);

/**
 * @param {object}  params
 * @param {Array}   params.blocks              — блоки КП (внутри области несут gazCargoItems)
 * @param {Array}   [params.products]          — строки раздела «Товары» как их ввёл менеджер
 * @param {number}  [params.vehiclesCount]     — число машин из модели загрузки
 * @param {boolean} params.isMskGazobetonScope — признак области, приходит готовым
 * @returns {object|null}
 */
export const buildGazOrderFacts = ({ blocks, products, vehiclesCount, isMskGazobetonScope } = {}) => {
  if (!isMskGazobetonScope) {
    return null;
  }

  const cargo = (Array.isArray(blocks) ? blocks : [])
    .flatMap((block) => (Array.isArray(block?.gazCargoItems) ? block.gazCargoItems : []));

  if (cargo.length === 0) {
    return null;
  }

  const factVolume = round3(sumBy(cargo, 'volume'));

  // Объём по каждой позиции — из того же состава груза, что и общий итог. Одна
  // позиция может лежать в двух блоках (полные машины и остаток), поэтому объёмы
  // с одинаковым именем складываются. Своего расчёта нет: суммируются те же
  // значения, что дают factVolume, — сумма по именам всегда равна ему.
  const volumeByName = cargo.reduce((acc, item) => {
    const name = String(item?.name || '').trim();
    const value = Number(item?.volume);
    if (!name || !Number.isFinite(value)) {
      return acc;
    }
    acc[name] = round3((acc[name] || 0) + value);
    return acc;
  }, {});
  const pallets = round3(sumBy(cargo, 'pallets'));
  // Штуки показываем, только если они посчитаны у всех позиций: частичная сумма
  // вводила бы в заблуждение.
  const hasPieces = cargo.every((item) => Number(item?.pieces) > 0);
  const pieces = hasPieces ? round3(sumBy(cargo, 'pieces')) : null;

  // Что просил клиент — только газобетонные строки заказа в тех же единицах.
  const requestedVolume = round3((Array.isArray(products) ? products : []).reduce((sum, product) => {
    const value = Number(product?.quantity);
    return Number.isFinite(value) && value > 0 ? sum + value : sum;
  }, 0));

  const palletRounding = requestedVolume > 0 ? round3(factVolume - requestedVolume) : 0;

  return {
    requestedVolume: requestedVolume > 0 ? requestedVolume : null,
    factVolume,
    // Разбивка того же объёма по позициям: имя позиции → её фактические м³.
    volumeByName,
    pallets: pallets > 0 ? pallets : null,
    pieces,
    // Прибавка объёма при округлении до полного поддона. Ноль — округлять было нечего.
    palletRounding: palletRounding > 0 ? palletRounding : 0,
    vehiclesCount: Number(vehiclesCount) > 0 ? Number(vehiclesCount) : null,
  };
};

export default buildGazOrderFacts;
